import React from "react";

import type { VariantProps } from "class-variance-authority";
import type { clampVariants } from "@picklebet/ui";

import cx from "classnames";
import { isPast } from "date-fns";
import { Clamp, TickIcon } from "@picklebet/ui";
import { Countdown } from "@/features/shell/components/shared/Countdown";
import type { BettingType } from "@/hooks/firestore/betting/types";

import * as styles from "./EventStatus.module.scss";

export const StatusEnum = {
  ACTIVE: "ACTIVE",
  SUSPENDED: "SUSPENDED",
  LIVE: "LIVE",
  SETTLED: "SETTLED",
  CANCELLED: "CANCELLED",
  DEACTIVATED: "DEACTIVATED",
  WON: "WON",
  LOST: "LOST",
  VOID: "VOID",
  RESULTED: "RESULTED",
  CLOSED: "CLOSED",
  INTERIM: "INTERIM",
  PROTEST: "PROTEST",
  FINAL: "FINAL",
  DONE: "DONE",
  JUMPED: "JUMPED",
  ABANDONED: "ABANDONED",
  COMPLETED: "COMPLETED",
  UNAVAILABLE: "UNAVAILABLE",
  HANDED_OVER: "HANDED_OVER",
  INVALID: "INVALID",
  OPEN: "OPEN",
} as const;

// Statuses for which a Live event remains visible/bettable in the UI.
export const LIVE_VISIBLE_STATUSES: StatusEnum[] = [
  StatusEnum.ACTIVE,
  StatusEnum.SUSPENDED,
  StatusEnum.UNAVAILABLE,
  StatusEnum.LIVE,
];

export type StatusEnum = (typeof StatusEnum)[keyof typeof StatusEnum];
export type ClampVariant = VariantProps<typeof clampVariants>["variant"];
export type ClampShape = VariantProps<typeof clampVariants>["shape"];

type EventStatusProps = {
  status: StatusEnum;
  scheduledStartTime: Date | null;
  bettingType?: BettingType;
  shape?: ClampShape;
  variant?: ClampVariant;
  clampClassName?: string;
  disabled?: boolean;
};

const statusToVariantMap: Record<StatusEnum, ClampVariant> = {
  [StatusEnum.SUSPENDED]: "neutral",
  [StatusEnum.COMPLETED]: "neutral",
  [StatusEnum.LIVE]: "neutral",
  [StatusEnum.SETTLED]: "light",
  [StatusEnum.CANCELLED]: "neutral",
  [StatusEnum.DEACTIVATED]: "neutral",
  [StatusEnum.WON]: "success",
  [StatusEnum.LOST]: "neutral",
  [StatusEnum.VOID]: "danger",
  [StatusEnum.RESULTED]: "light",
  [StatusEnum.CLOSED]: "dangerSoft",
  [StatusEnum.INTERIM]: "light",
  [StatusEnum.PROTEST]: "danger",
  [StatusEnum.FINAL]: "light",
  [StatusEnum.DONE]: "success",
  [StatusEnum.JUMPED]: "dangerSoft",
  [StatusEnum.ABANDONED]: "disabled",
  [StatusEnum.ACTIVE]: "neutral",
  [StatusEnum.UNAVAILABLE]: "disabled",
  [StatusEnum.HANDED_OVER]: "neutral",
  [StatusEnum.INVALID]: "disabled",
  [StatusEnum.OPEN]: "neutral",
};

export function isLiveForDisplay(
  status: StatusEnum,
  bettingType: BettingType | undefined,
  scheduledStartTime: Date | null,
) {
  const hasStarted = scheduledStartTime ? isPast(scheduledStartTime) : false;
  const isBettingLive =
    bettingType === "Live" &&
    hasStarted &&
    LIVE_VISIBLE_STATUSES.includes(status);
  return isBettingLive || (status === "SUSPENDED" && hasStarted);
}

const isValidStatus = (value: string): boolean =>
  Object.values(StatusEnum).includes(value as StatusEnum);

const getClampVariant = (status: StatusEnum): ClampVariant => {
  return statusToVariantMap[status] || "neutral";
};

const EventStatus = ({
  status,
  shape,
  scheduledStartTime,
  bettingType,
  clampClassName,
  variant: variantOverride,
}: EventStatusProps) => {
  const hasStarted = scheduledStartTime ? isPast(scheduledStartTime) : false;
  const variant = variantOverride || getClampVariant(status);

  const isBettingLive =
    bettingType === "Live" &&
    hasStarted &&
    LIVE_VISIBLE_STATUSES.includes(status);

  const shouldShowStatus =
    isBettingLive ||
    (status !== StatusEnum.ACTIVE &&
      status !== StatusEnum.OPEN &&
      isValidStatus(status));

  // hasStarted is a fallback for when bettingType isn't "Live" yet.
  const adjustedStatus = isLiveForDisplay(
    status,
    bettingType,
    scheduledStartTime,
  )
    ? "LIVE"
    : status;

  const pulseStatuses: StatusEnum[] = ["CLOSED", "INTERIM"];

  if (shouldShowStatus) {
    return (
      <Clamp
        variant={variant}
        shape={shape}
        className={cx(clampClassName, {
          [styles.cancelled]: adjustedStatus === "CANCELLED",
          [styles.pulse]: pulseStatuses.includes(adjustedStatus),
          [styles.live]: adjustedStatus === "LIVE",
        })}
      >
        {adjustedStatus === "FINAL" ? (
          <TickIcon className={styles.tick} />
        ) : null}
        {adjustedStatus}
      </Clamp>
    );
  }

  return (
    <Countdown
      scheduledStartTime={scheduledStartTime}
      shape={shape}
      variant={variant}
      clampClassName={clampClassName}
    />
  );
};

export { EventStatus, EventStatusProps };
